import React, { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import { useTheme } from '../ThemeContext';
import ModalLayer from './ModalLayer';
import PropertyContacts from './PropertyContacts';
import ErrorBoundary from './ErrorBoundary';
import { NOT_PROVIDED, clientDetailModel, mergeClientSystems } from '../shared/clientDetail';
import { WRAP_TEXT } from '../shared/wrapText';
import { StoredPropertyPhoto } from './PropertyPhoto';
import { readBrand, readInstallYear } from '../shared/inventoryItem';
import { ClientProsList, CLIENT_PROS_HEADING } from './HomeownerPros';
import { AGENT_PRO_COLUMNS, shapeProsForAgent } from '../shared/homeownerPros';
import { HOME_REPORT_AGENT_PLAN_MESSAGE } from '../shared/homeownerEntitlement';
import AgentSharedDocuments from './AgentSharedDocuments';

const AGENT_DOCUMENT_COLUMNS = 'id, client_id, title, file_name, file_path, category, created_at, shared_with_agent, created_by, folder_id';

function documentQueryNeedsLegacyColumns(error) {
  const message = error && error.message ? String(error.message) : '';
  return /column|schema cache|does not exist/i.test(message);
}

async function loadAgentDocuments(clientId) {
  const primary = await supabase
    .from('client_documents')
    .select(AGENT_DOCUMENT_COLUMNS)
    .eq('client_id', clientId)
    .order('created_at', { ascending: false });
  if (primary && primary.error && documentQueryNeedsLegacyColumns(primary.error)) {
    return supabase
      .from('client_documents')
      .select('id, client_id, title, file_name, category, created_at')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false });
  }
  return primary;
}

async function loadClientDetailRecords(clientId) {
  const [tasksResult, docsResult, helpResult, prosResult] = await Promise.all([
    supabase
      .from('client_tasks')
      .select('id, completed, due_date, snoozed_until, client_notes, task_name, task:tasks(title)')
      .eq('client_id', clientId),
    loadAgentDocuments(clientId),
    supabase
      .from('task_help_requests')
      .select('id, client_id, client_task_id, task_title, message, status, created_at')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false }),
    supabase
      .from('homeowner_pros')
      .select(AGENT_PRO_COLUMNS)
      .eq('client_id', clientId),
  ]);
  const documents = ((docsResult && docsResult.data) || []).filter((doc) => (
    !doc || !doc.client_id || doc.client_id === clientId
  ));
  return {
    tasks: (tasksResult && tasksResult.data) || [],
    documents,
    helpRequests: (helpResult && !helpResult.error && helpResult.data) || [],
    homeownerPros: shapeProsForAgent((prosResult && !prosResult.error && prosResult.data) || []),
    error: (tasksResult && tasksResult.error) || (docsResult && docsResult.error) || null,
  };
}

function FieldList({ fields, colors }) {
  return (
    <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'minmax(7rem, 34%) minmax(0, 1fr)', gap: '0.4rem 0.75rem', minWidth: 0, maxWidth: '100%' }}>
      {fields.map((field) => (
        <React.Fragment key={field.label}>
          <dt style={{ margin: 0, color: colors.textSecondary, fontSize: '0.85rem', ...WRAP_TEXT }}>{field.label}</dt>
          <dd style={{ margin: 0, color: colors.text, fontSize: '0.95rem', ...WRAP_TEXT }}>{field.value}</dd>
        </React.Fragment>
      ))}
    </dl>
  );
}

const ClientDetailPanel = ({ client, systems, onClose, onOpenTasks, onEdit, onInvite, inviteLabel, onRequestReport }) => {
  const { colors } = useTheme();
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [tasks, setTasks] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [helpRequests, setHelpRequests] = useState([]);
  const [homeownerPros, setHomeownerPros] = useState([]);
  const [recordNote, setRecordNote] = useState('');

  useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape' && onClose) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const clientId = client && client.id;
    setTasks([]);
    setDocuments([]);
    setHelpRequests([]);
    setHomeownerPros([]);
    setRecordNote('');
    if (!clientId) return undefined;
    let cancelled = false;
    loadClientDetailRecords(clientId)
      .then((result) => {
        if (cancelled) return;
        setTasks(result.tasks);
        setDocuments(result.documents);
        setHelpRequests(result.helpRequests || []);
        setHomeownerPros(result.homeownerPros || []);
        setRecordNote(result.error ? 'Tasks and documents could not be loaded.' : '');
      })
      .catch(() => {
        if (!cancelled) setRecordNote('Tasks and documents could not be loaded.');
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  if (!client) return null;
  const model = clientDetailModel(client, { systems, tasks, documents });
  const name = model.header[0].value;

  return (
    <ModalLayer
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 4000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: narrow ? 'flex-start' : 'center',
        justifyContent: 'center',
        padding: narrow ? 0 : '1rem',
      }}
    >
      <div
        className="app-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="client-detail-title"
        onClick={(event) => event.stopPropagation()}
        style={{
          background: colors.surface,
          color: colors.text,
          width: narrow ? '100%' : 'min(760px, 100%)',
          maxWidth: '100%',
          minWidth: 0,
          maxHeight: narrow ? '100%' : '92vh',
          height: narrow ? '100%' : 'auto',
          overflowX: 'hidden',
          overflowY: 'auto',
          borderRadius: narrow ? 0 : '1rem',
          border: `1px solid ${colors.border}`,
          boxSizing: 'border-box',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div className="app-modal-header" style={{ background: colors.surface, alignItems: 'center' }}>
          <h2 id="client-detail-title" style={{ margin: 0, fontSize: '1.35rem', color: colors.text, ...WRAP_TEXT }}>{name}</h2>
          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(client)}
              style={{
                background: 'transparent',
                color: colors.text,
                border: `1px solid ${colors.border}`,
                borderRadius: '0.5rem',
                padding: '0.7rem 1rem',
                minHeight: '44px',
                fontWeight: 650,
                cursor: 'pointer',
                flex: '0 0 auto',
              }}
            >
              Edit client
            </button>
          )}
          <button
            type="button"
            className="app-modal-close"
            aria-label="Close"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: colors.textSecondary,
              fontSize: '1.4rem',
              cursor: 'pointer',
              minWidth: '44px',
              minHeight: '44px',
            }}
          >
            ×
          </button>
        </div>
        {typeof onRequestReport === 'function' && client && client.entitled === false ? (
          <p style={{ margin: '1rem 0' }}>{HOME_REPORT_AGENT_PLAN_MESSAGE}</p>
        ) : typeof onRequestReport === 'function' ? (
          <div style={{ margin: '1rem 0' }}>
            <button
              type="button"
              onClick={() => onRequestReport(client)}
              style={{
                background: '#0f766e',
                color: 'white',
                border: 'none',
                borderRadius: '0.5rem',
                padding: '0.7rem 1rem',
                minHeight: '44px',
                fontWeight: 650,
                cursor: 'pointer',
              }}
            >
              Request Home History Report
            </button>
          </div>
        ) : null}
        {onInvite && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', margin: '1rem 0' }}>
            {onInvite && (
              <button
                type="button"
                onClick={() => onInvite(client)}
                style={{
                  background: '#0f766e',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.5rem',
                  padding: '0.7rem 1rem',
                  minHeight: '44px',
                  fontWeight: 650,
                  cursor: 'pointer',
                }}
              >
                {inviteLabel || 'Invite'}
              </button>
            )}
          </div>
        )}

        <section style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: colors.text }}>Maintenance answers</h3>
          <FieldList fields={model.plan || []} colors={colors} />
        </section>

        <section style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: colors.text }}>Homeowner</h3>
          <FieldList fields={model.header} colors={colors} />
        </section>

        <section style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: colors.text }}>Property Details</h3>
          <StoredPropertyPhoto
            stored={model.photoUrl}
            alt=""
            style={{ width: '100%', maxHeight: '240px', objectFit: 'cover', borderRadius: '0.5rem', marginBottom: '0.75rem' }}
            fallback={<p style={{ margin: '0 0 0.75rem', color: colors.textSecondary }}>Photo: {NOT_PROVIDED}</p>}
          />
          <FieldList
            fields={model.property}
            colors={colors}
          />
          <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {mergeClientSystems(client, systems).length === 0 ? (
              <p style={{ margin: 0, color: colors.textSecondary }}>No home systems yet.</p>
            ) : mergeClientSystems(client, systems).map((system) => {
              const brand = readBrand(system.data);
              const year = readInstallYear(system.data);
              return (
                <div key={system.id} style={{ padding: '0.75rem 0.9rem', borderRadius: '0.75rem', border: `1px solid ${colors.border}` }}>
                  <div style={{ fontWeight: 650, color: colors.text }}>{system.name}</div>
                  {(brand || year) && (
                    <div style={{ fontSize: '0.85rem', color: colors.textSecondary, marginTop: '0.2rem' }}>
                      {[brand, year && `Installed ${year}`].filter(Boolean).join(' · ')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: colors.text }}>Features</h3>
          {model.features.length === 0 ? (
            <p style={{ margin: 0, color: colors.text }}>{NOT_PROVIDED}</p>
          ) : (
            <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
              {model.features.map((feature) => (
                <li key={feature} style={{ marginBottom: '0.25rem', color: colors.text }}>{feature}</li>
              ))}
            </ul>
          )}
        </section>

        <section style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: colors.text }}>Tasks</h3>
          {recordNote && <p style={{ color: colors.warning || '#f59e0b' }}>{recordNote}</p>}
          <p style={{ margin: '0 0 0.75rem', color: colors.text }}>
            Pending {model.tasks.pending} · Overdue {model.tasks.overdue} · Completed {model.tasks.completed}
          </p>
          {tasks.filter((task) => task && task.client_notes).map((task) => (
            <p key={task.id} style={{ margin: '0 0 0.5rem', color: colors.text }}>
              <strong>{task.task_name || (task.task && task.task.title) || 'Task'}:</strong> {task.client_notes}
            </p>
          ))}
          {helpRequests.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <h4 style={{ margin: '0.5rem 0', fontSize: '0.95rem', color: colors.text }}>Help requests</h4>
              {helpRequests.map((request) => (
                <p key={request.id} style={{ margin: '0 0 0.5rem', color: colors.text }}>
                  <strong>{request.task_title || 'Task'}:</strong> {request.message}
                </p>
              ))}
            </div>
          )}
          <button
            type="button"
            onClick={() => onOpenTasks && onOpenTasks(client)}
            style={{
              background: '#59c4c4',
              color: 'white',
              border: 'none',
              borderRadius: '0.5rem',
              padding: '0.7rem 1rem',
              minHeight: '44px',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            View tasks
          </button>
        </section>

        <section style={{ marginBottom: '1.5rem' }}>
          <AgentSharedDocuments
            documents={documents}
            colors={colors}
            onOpen={async (doc) => {
              try {
                const signed = await supabase.storage.from('client-documents').createSignedUrl(doc.file_path, 3600);
                const url = signed && signed.data && signed.data.signedUrl;
                if (!url || (signed && signed.error)) {
                  setRecordNote('That document could not be opened.');
                  return;
                }
                window.open(url, '_blank', 'noopener,noreferrer');
              } catch (error) {
                setRecordNote('That document could not be opened.');
              }
            }}
          />
        </section>

        <section style={{ marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: colors.text }}>{CLIENT_PROS_HEADING}</h3>
          <ClientProsList pros={homeownerPros} colors={colors} />
        </section>

        <section>
          <h3 style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: colors.text }}>Utility and Service Providers</h3>
          <ErrorBoundary>
            <PropertyContacts clientId={client.id} colors={colors} />
          </ErrorBoundary>
        </section>
      </div>
    </ModalLayer>
  );
};

export default ClientDetailPanel;
export { loadClientDetailRecords };
